import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import VisibilityIcon from "@material-ui/icons/Visibility";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import Confirm from "components/Confirm";
import TypeChip from "./TypeChip";
import TemplateViewDialog from "./TemplateViewDialog";
import { getFormattedTimeStamp } from "utils/dateUtils";
import { StorageNotificationTemplate, getActiveTypes } from "../typeConfig";
import StarIcon from "@material-ui/icons/Star";
import StarBorderIcon from "@material-ui/icons/StarBorder";

type Props = {
  template: StorageNotificationTemplate;
  deleting?: boolean;
  togglingDefault?: boolean;
  onDelete: (template: StorageNotificationTemplate) => void;
  onToggleDefault: (id: string, isCurrentDefault: boolean) => void;
};

export default function TemplateRow(props: Props) {
  const { template, deleting, togglingDefault, onDelete, onToggleDefault } =
    props;
  const classes = useStyles();
  const { t } = useTranslation("storage");
  const history = useHistory();
  const activeTypes = getActiveTypes(template);
  const [viewOpen, setViewOpen] = useState(false);
  const updatedAt = t("common:updatedAt", {
    date: getFormattedTimeStamp(template.updatedAt, true),
  });

  if (deleting) {
    return (
      <div className={classes.row}>
        <div className={classes.info}>
          <Skeleton variant="text" animation="wave" width="60%" height={22} />
          <div className={classes.chips}>
            <Skeleton
              variant="rect"
              animation="wave"
              width={36}
              height={20}
              style={{ borderRadius: 10 }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.row}>
      <div className={classes.info}>
        <div className={classes.nameRow}>
          <Typography
            variant="body2"
            className={classes.name}
            title={template.name}
          >
            {template.name}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {updatedAt}
          </Typography>
        </div>
        <div className={classes.chips}>
          {activeTypes.map(type => (
            <TypeChip key={type} type={type} />
          ))}
        </div>
      </div>
      <div className={classes.actions}>
        <Tooltip
          title={
            template.defaultTemplate
              ? t("notificationTemplates.list.removeDefault")
              : t("notificationTemplates.list.markDefault")
          }
          arrow
        >
          <span>
            <IconButton
              size="small"
              disabled={togglingDefault}
              onClick={() => {
                onToggleDefault(template.id, template.defaultTemplate);
              }}
            >
              {togglingDefault ? (
                <CircularProgress size={15} />
              ) : template.defaultTemplate ? (
                <StarIcon fontSize="small" color="primary" />
              ) : (
                <StarBorderIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t("notificationTemplates.list.viewTooltip")} arrow>
          <IconButton size="small" onClick={() => setViewOpen(true)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("notificationTemplates.list.editTooltip")} arrow>
          <IconButton
            size="small"
            onClick={() =>
              history.push(
                `/integrations/notification-templates/${template.id}/edit`
              )
            }
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Confirm
          message={t("notificationTemplates.list.deleteConfirm", {
            name: template.name,
          })}
          onAccept={() => onDelete(template)}
          actionable={openConfirm => (
            <Tooltip
              title={t("notificationTemplates.list.deleteTooltip")}
              arrow
            >
              <IconButton size="small" onClick={openConfirm}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        />
      </div>
      {viewOpen && (
        <TemplateViewDialog
          template={template}
          onClose={() => setViewOpen(false)}
        />
      )}
    </div>
  );
}

const useStyles = makeStyles(theme => ({
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 16px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": { borderBottom: "none" },
    "&:nth-child(even)": { backgroundColor: "#f9fafb" },
    "&:hover": { backgroundColor: theme.palette.action.hover },
    transition: "background-color 150ms",
  },
  info: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    minWidth: 0,
    flex: 1,
  },
  nameRow: {
    display: "flex",
    alignItems: "baseline",
    gap: 8,
    minWidth: 0,
  },
  name: {
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 2,
  },
  actions: {
    display: "flex",
    gap: 4,
    marginLeft: 12,
  },
}));
